define((require) => {
    "use strict";

    const {defer, extend}               = require("underscore");
    const EditorConstants               = require("editor/ui/experience/editor/EditorConstants");
    const WidgetGroup                   = require("ui/editor/WidgetGroup");
    const EditorEvents                  = require("shared/services/EditorEvents");
    const MathUtil                      = require("shared/util/MathUtil").default;
    const Preconditions                 = require("shared/util/Preconditions");
    const template                      = require("ui/experience/widgets/SizeAndPositionPropertiesView.tpl");
    const {camelize}                    = require("shared/util/StringUtils");
    const AbstractWidgetPropertiesView  = require("ui/experience/widgets/AbstractWidgetPropertiesView");
    const {Session, Keys}               = require("shared/services/Storage");
    const Distribution                  = require("util/Distribution");
    const Alignment                     = require("util/Alignment");

    const EXPAND_PROPERTY           = "sizeAndPositionExpanded";
    const ALIGNED_TO_SCENE_STORAGE  = "alignToScene";

    const compareByLowestLayerIndex     = (a, b) => a.get("layerIndex") > b.get("layerIndex");
    const compareByHighestLayerIndex    = (a, b) => a.get("layerIndex") < b.get("layerIndex");

    /**
     * The properties view for size and position information.
     *
     * @class SizeAndPositionPropertiesView
     * @constructor
     *
     * @cfg {AbstractWidgetModel} model The widget being edited.
     * @cfg {boolean} [hideSizeControls] If true, the size controls will be hidden.
     * @cfg {boolean} [hideRotation] If true, the rotation control will be hidden.
     */
    return AbstractWidgetPropertiesView.extend({
        expandProperty: EXPAND_PROPERTY,

        _experience: null,

        positionInputChanged: false,
        dimensionsInputChanged: false,
        rotationInputChanged: false,

        template,

        ui: {
            container: ".size-position-properties",
            legend: "[data-target=\".size-position-properties\"]",
            alignToScene: ".align-to-scene",
            alignToSceneContainer: ".align-to-scene-container",
            fitToScene: ".fit-to-scene",
            rotation: ".rotation",
            grouping: ".grouping",
            groupButton: ".grouping__group",
            ungroupButton: ".grouping__ungroup",
            arrangeControls: ".arrange-controls",
            alignControls: ".align-controls"
        },

        bindings: {
            ".left": {
                observe: "left",
                onGet: "onGet",
                onSet: "onSet"
            },
            ".top": {
                observe: "top",
                onGet: "onGet",
                onSet: "onSet"
            },
            ".width": {
                observe: "width",
                onGet: "onGet",
                onSet: "onSet"
            },
            ".height": {
                observe: "height",
                onGet: "onGet",
                onSet: "onSet"
            },
            ".rotation": {
                observe: "rotation",
                onGet: "_parseRotation",
                onSet: "onSet"
            }
        },

        events: {
            "click .fit-to-scene":      "_fitToScene",
            "keyup .rotation":          "_changeRotation",
            "click @ui.groupButton":    "_groupingHandler",
            "click @ui.ungroupButton":  "_ungroupingHandler",
            "change .align-to-scene":   "_alignToSceneChangeHandler"
        },

        templateHelpers() {
            return {
                showSizeControls:   !this.options.hideSizeControls,
                showRotation:       !this.options.hideRotation,
                showLineControls:   !!this.options.showLineControls,
                showWidthControls:  this.options.showWidthControls,
                showGroupingControls: !this.options.hideGrouping,
                editorConstants:    EditorConstants
            };
        },


        onGet(value, options) {
            switch (options.observe) {
                case "left":
                case "width":
                    return MathUtil.round(value * this._experience.get("width") / 100, 0);
                case "top":
                case "height":
                    return MathUtil.round(value * this._experience.get("height") / 100, 0);
                default:
                    // TODO Candidate for refactoring during MOS-2934.
                    Preconditions.warn(`DefaultSwitchWarn - Position '${options.observe}' not recognized.`);
                    break;
            }
        },

        onSet(value, options) {
            this._trackingChanges(options.observe);
            switch (options.observe) {
                case "left":
                case "width":
                    return MathUtil.roundToContain(value / this._experience.get("width") * 100, this._experience.get("width"));
                case "top":
                case "height":
                    return MathUtil.roundToContain(value / this._experience.get("height") * 100, this._experience.get("height"));
                case "rotation":
                    return Number(value);
                default:
                    // TODO Candidate for refactoring during MOS-2934.
                    Preconditions.warn(`DefaultSwitchWarn - Position '${options.observe}' not recognized.`);
                    break;
            }
        },

        _trackingChanges(type) {
            switch (type) {
                case "left":
                case "top":
                    this.positionInputChanged = true;
                    break;
                case "width":
                case "height":
                    this.dimensionsInputChanged = true;
                    break;
                case "rotation":
                    this.rotationInputChanged = true;
                    break;
                default:
                    // TODO Candidate for refactoring during MOS-2934.
                    Preconditions.warn(`DefaultSwitchWarn - Position '${type}' not recognized.`);
                    break;
            }
        },

        _fitToScene() {
            this.model.fitToScene(this._experience);
            this._triggerEventFitToScene();
        },

        /**
         * @private
         *
         * Sends an event to MixPanel when a there's a click on the button Fit proportionally.
         * @fires EditorEvents#update:widget
         * @type {Object}
         * @property {String} widgetType                The type of the widget.
         * @property {String} updatedProperty           The property of the widget which has been modified.
         * @property {Number} experienceId              The experience id.
         * @property {Number} experienceVersion         The experience version.
         * @property {Number} experiencePushedVersion   The experience pushed version sceneNumber.
         * @property {Number} sceneNumber               The scene number.
         * @property {String} triggerOrigin             The origin of the event.
         */
        _triggerEventFitToScene() {
            const viewModel         = this.viewScope.viewModel;
            const eventProperties   = {
                widgetType:                 this.model.get("type"),
                updatedProperty:            "fitProportionally",
                experienceId:               this._experience.get("id"),
                experienceVersion:          this._experience.get("version"),
                experiencePushedVersion:    this._experience.get("pushedVersion"),
                sceneNumber:                viewModel.get("sceneNumber"),
                triggerOrigin:              "WidgetMenuFitProportionallyButton"
            };

            EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
        },

        /**
         * Handler when the rotation input changes
         *
         * @param {JQuery<Event>} event - The event information when rotation input field is modified.
         */
        _changeRotation(event) {
            const position = this._experience.getPercentagePosition(this._calculateNewPosition(event.target.value));

            this.model.set({
                left:   position.left,
                top:    position.top
            });
        },

        /**
         * Parses the rotation value.
         */
        _parseRotation(value) {
            let parsedValue = value;
            if (value < 0) {
                parsedValue += 360;
            }
            if (value > 360) {
                parsedValue -= 360;
            }
            return MathUtil.round(parsedValue, 1);
        },

        /**
         * When rotating the widget from the sidebar, we need to recalculate a
         * new position for it. This is done by calculating the offset for the
         * center, and offsetting the position of the widget in the opposite
         * direction.
         */
        _calculateNewPosition(newRotation) {
            const viewModel = this.viewScope.viewModel;
            const position = this._experience.getPixelPosition({
                top:    this.model.get("top"),
                left:   this.model.get("left")
            });
            const oldRotation   = this.model.get("rotation");
            const widgetPosition = {x: position.left, y: position.top};
            let size;
            let width;
            let height;
            let widgetCenter;
            let oldCenter;
            let newCenter;

            if (this.model.has("width") && this.model.has("height")) {
                size = this._experience.getPixelSize({
                    width: this.model.get("width"),
                    height: this.model.get("height")
                });
                width   = size.width;
                height  = size.height;
            } else {
                const firstWidgetElement = viewModel.selection.get("widgets")[0].$el;
                width  = firstWidgetElement.width();
                height = firstWidgetElement.height();
            }

            widgetCenter = {
                x: position.left + width / 2,
                y: position.top + height / 2
            };

            oldCenter = MathUtil.rotatePoint(widgetCenter, widgetPosition, oldRotation);
            newCenter = MathUtil.rotatePoint(widgetCenter, widgetPosition, newRotation);

            return {
                left:   MathUtil.roundToContain(position.left - (newCenter.x - oldCenter.x), this._experience.get("width")),
                top:    MathUtil.roundToContain(position.top - (newCenter.y - oldCenter.y), this._experience.get("height"))
            };
        },

        onRender() {
            this.__super();

            const viewModel         = this.viewScope.viewModel;
            const scene             = viewModel.get("scene");
            const selectedModels    = viewModel.selection.get("models");


            this._experience = viewModel.get("experience");
            // Through out the code we treat group of widgets as the one widget,
            // but we can't rely on this here:
            // grouping options must be hidden for single widget only.
            // That's why we check here if selection contains a group.
            const selectionHasGroups = scene.canUngroupWidgets(selectedModels);
            if (viewModel.selection.isSingle() && !selectionHasGroups) {
                this.ui.grouping.hide();
            } else {
                this.ui.groupButton.prop("disabled", !scene.canGroupWidgets(selectedModels));
                this.ui.ungroupButton.prop("disabled", !selectionHasGroups);
            }

            this._initializeArrangeDropdown();
            this._initializeAlignDropdown();
            this._reloadSelect2Lists();

            if (selectedModels.length > 1) {
                const isAlignedToSceneChecked = Session.get(Keys.ALIGNED_TO_SCENE_CHECKBOX, ALIGNED_TO_SCENE_STORAGE, true);

                this.ui.alignToScene.prop({
                    disabled:   false,
                    checked:    isAlignedToSceneChecked
                });
            } else {
                Session.unset(Keys.ALIGNED_TO_SCENE_CHECKBOX, ALIGNED_TO_SCENE_STORAGE);
                this.ui.alignToScene.prop("disabled", true);
                this._allowDistribution(false);
            }
        },

        _initializeArrangeDropdown() {
            this.ui.arrangeControls.select2({
                placeholder:                "Arrange",
                dropdownAutoWidth:          true,
                formatResult:               this._formatSelectItems,
                minimumResultsForSearch:    -1,
                dropdownCssClass:           "positioning-controls-selector"
            });
        },

        _initializeAlignDropdown() {
            this.ui.alignControls.select2({
                placeholder:                "Align",
                dropdownAutoWidth:          true,
                formatResult:               this._formatSelectItems,
                minimumResultsForSearch:    -1,
                dropdownCssClass:           "positioning-controls-selector"
            });
        },

        onShow() {
            // TODO: This feels kind of hacky
            // The logic of not having the animation of the PropertiesView should live
            // in the AbstractWidgetPropertiesView and be configurable.
            if (this._shouldExpandOnRender()) {
                this._$accordionTarget
                    .removeClass("collapsing")
                    .addClass("collapse in")
                    .height("");
            } else {
                this.__super();
            }

            this.ui.arrangeControls.on("select2-selecting", this._onArrangementSelect.bind(this));
            this.ui.arrangeControls.on("select2-close",     this._onSelect2Close.bind(this));
            this.ui.alignControls.on("select2-selecting",   this._onAlignDistributeSelect.bind(this));
            this.ui.alignControls.on("select2-close",       this._onSelect2Close.bind(this));
        },

        _onSelect2Close() {
            this.ui.arrangeControls.select2("val", "");
            this.ui.alignControls.select2("val", "");

            // Select2 keeps the focus even after the onClose event, which doesn't let the undo/redo
            // functionality work properly since the focus has to be on the body.
            defer(() => { document.activeElement.blur(); });
        },

        /**
         * Handler for the align to scene checkbox.
         *
         * @param {JQuery<Event>} event - Event containing the alignToScene checkbox data.
         */
        _alignToSceneChangeHandler(event) {
            const models = this.viewScope.viewModel.selection.get("models");

            // We keep the state of alignedToScene if it's unchecked.
            if (!event.target.checked) {
                Session.update(Keys.ALIGNED_TO_SCENE_CHECKBOX, ALIGNED_TO_SCENE_STORAGE, false);
            }
            if (models.length === 2) {
                this._allowDistribution(event.target.checked);
            } else {
                this._allowDistribution(true);
            }
        },

        /**
         * Grays out or activate the Distribution option in the align dropdown.
         *
         * @param {Boolean} isActivated if the distribution should be activated in the drop down
         */
        _allowDistribution(isActivated) {
            this.ui.alignControls.find(`option[value=${EditorConstants.DISTRIBUTE_HORIZONTALLY.value}]`).prop("disabled", !isActivated);
            this.ui.alignControls.find(`option[value=${EditorConstants.DISTRIBUTE_VERTICALLY.value}]`).prop("disabled", !isActivated);

            this._initializeAlignDropdown();
        },

        /**
         * @private
         *
         * The open/close allows to insert the select2 dropdown into the DOM.
         * Which will actually preload the icons instead of loading them,
         * on the first open of the dropdown by the user.
         */
        _reloadSelect2Lists() {
            this.ui.arrangeControls.select2("open");
            this.ui.arrangeControls.select2("close");
            this.ui.alignControls.select2("open");
            this.ui.alignControls.select2("close");
            document.activeElement.blur();
        },

        _groupingHandler() {
            WidgetGroup.group({eventProperties: {triggerOrigin: "rightPanel"}});
        },

        _ungroupingHandler() {
            WidgetGroup.ungroup({eventProperties: {triggerOrigin: "rightPanel"}});
        },

        /**
         * @private
         *
         * Create the template for the option of the arrangement.
         * If a data-css-class attribute is set to the option then it will be used
         * when creating the span.
         *
         * @param {Object} currentOption The select2 element
         * @returns {String} The template used by Select2 to render the @see currentOption
         */
        _formatSelectItems(currentOption) {
            const elementDataSet    = currentOption.element[0].dataset;
            const cssClass          = elementDataSet.cssClass;

            // In order to get a placeholder in Select2,
            // an empty option has to be inserted in front.
            return cssClass
                ? `<span class="option-icon ${cssClass}" ></span>${currentOption.text}`
                : currentOption.text;
        },

        /**
         * Event handler when a element has been selected in the dropdown
         *
         * @param {Select2JQueryEventObject} e - Event containing the selected arrangement option.
         * @private
         */
        _onArrangementSelect(e) {
            const viewModel     = this.viewScope.viewModel;
            const scene         = viewModel.get("scene");
            const widgets       = viewModel.selection.get("models");
            let sendBack        = false;
            let bringToFront    = false;
            let selectedOption;

            switch (+e.val) {
                case EditorConstants.BRING_TO_FRONT.value:
                    widgets.sort(compareByLowestLayerIndex);
                    selectedOption = EditorConstants.BRING_TO_FRONT;
                    break;
                case EditorConstants.SEND_BACK.value:
                    widgets.sort(compareByHighestLayerIndex);
                    selectedOption = EditorConstants.SEND_BACK;
                    break;
                case EditorConstants.BRING_FORWARD.value:
                    widgets.sort(compareByHighestLayerIndex);

                    if (widgets.length > 1) {
                        const highestIndex = scene.getHighestIndex();

                        if (widgets[0].get("layerIndex") === highestIndex) {
                            bringToFront = true;
                        }
                    }
                    selectedOption = EditorConstants.BRING_FORWARD;
                    break;
                case EditorConstants.SEND_BACKWARD.value:
                    widgets.sort(compareByLowestLayerIndex);

                    if (widgets.length > 1) {
                        sendBack = widgets[0].get("layerIndex") === 0;
                    }
                    selectedOption = EditorConstants.SEND_BACKWARD;
                    break;
                default:
                    Preconditions.warn(`DefaultSwitchWarn - ArrangeDropDown '${e.val}'  not recognized.`);
                    break;
            }

            widgets.forEach((widgetModel) => {
                const siblingWidgets = widgetModel.getGroupSiblings(this.viewScope.viewModel.get("scene").get("widgets"));
                scene.orderWidget({widgetModel, to: selectedOption.value, siblingWidgets});
            });

            if (sendBack) {
                scene.orderWidget({widgetModel: widgets[0], to: EditorConstants.SEND_BACK.value});
            }

            if (bringToFront) {
                scene.orderWidget({widgetModel: widgets[0], to: EditorConstants.BRING_TO_FRONT.value});
            }

            this._triggerPositioningEvent(selectedOption.text, "WidgetMenuArrangeDropdown");
        },

        /**
          * Create a container from the experience widget and height
          *
          * @returns {ContainerSize}
          */
        _getContainerSizeFromExperience() {
            const experienceWidth   = this._experience.get("width");
            const experienceHeight  = this._experience.get("height");

            return {
                right:  experienceWidth,
                left:   0,
                top:    0,
                bottom: experienceHeight,
                verticalCenter: experienceHeight / 2,
                horizontalCenter: experienceWidth / 2
            };
        },

        /**
         * Create the container size out of the selection of widgets
         *
         * @param {Object[]} widgets - The collection of widgets.
         * @returns {ContainerSize}
         */
        _getContainerSizeFromWidgets(widgets) {
            const furthestPoints = {
                top:    Math.min(...widgets.map(({boundingBox}) => boundingBox.top)),
                bottom: Math.max(...widgets.map(({boundingBox}) => boundingBox.bottom)),
                left:   Math.min(...widgets.map(({boundingBox}) => boundingBox.left)),
                right:  Math.max(...widgets.map(({boundingBox}) => boundingBox.right))
            };

            return {
                bottom: furthestPoints.bottom,
                left:   furthestPoints.left,
                right:  furthestPoints.right,
                top:    furthestPoints.top,
                verticalCenter: (furthestPoints.bottom + furthestPoints.top) / 2,
                horizontalCenter: (furthestPoints.right + furthestPoints.left) / 2
            };
        },

        _mapWidgetToModelAndContainer(widget) {
            return {
                boundingBox:    widget.getBoundingBox(),
                rect:           widget.getRect(),
                model:          widget.model
            };
        },

        /**
         * Handler for the Align dropdown
         * Apply the distribution or the alignement to the widget or a selection widgets.
         *
         * @param {Select2JQueryEventObject} e - The event fired on the dropdown
         * @private
         */
        _onAlignDistributeSelect(e) {
            let actionName;
            const viewModel         = this.viewScope.viewModel;
            const isAlignedToScene  = this.ui.alignToScene.prop("checked");
            const selectedWidgets   = viewModel.selection.get("widgets");
            const widgets           = selectedWidgets.map(this._mapWidgetToModelAndContainer);
            const containerSize     = isAlignedToScene
                ? this._getContainerSizeFromExperience()
                : this._getContainerSizeFromWidgets(widgets);
            const experienceSize    = {
                width:  this._experience.get("width"),
                height: this._experience.get("height")
            };

            switch (+e.val) {
                case EditorConstants.ALIGN_LEFT.value: {
                    Alignment.alignLeft(widgets, containerSize.left, experienceSize);
                    actionName = EditorConstants.ALIGN_LEFT.text;
                    break;
                }

                case EditorConstants.ALIGN_CENTER.value: {
                    Alignment.alignCenter(widgets, containerSize.horizontalCenter, experienceSize);
                    actionName = EditorConstants.ALIGN_CENTER.text;
                    break;
                }

                case EditorConstants.ALIGN_RIGHT.value: {
                    Alignment.alignRight(widgets, containerSize.right, experienceSize);
                    actionName = EditorConstants.ALIGN_RIGHT.text;
                    break;
                }

                case EditorConstants.ALIGN_TOP.value: {
                    Alignment.alignTop(widgets, containerSize.top, experienceSize);
                    actionName = EditorConstants.ALIGN_TOP.text;
                    break;
                }

                case EditorConstants.ALIGN_MIDDLE.value: {
                    Alignment.alignMiddle(widgets, containerSize.verticalCenter, experienceSize);
                    actionName = EditorConstants.ALIGN_MIDDLE.text;
                    break;
                }

                case EditorConstants.ALIGN_BOTTOM.value: {
                    Alignment.alignBottom(widgets, containerSize.bottom, experienceSize);
                    actionName = EditorConstants.ALIGN_BOTTOM.text;
                    break;
                }

                case EditorConstants.DISTRIBUTE_HORIZONTALLY.value: {
                    Distribution.distributeHorizontally(widgets, containerSize, experienceSize, isAlignedToScene);
                    actionName = EditorConstants.DISTRIBUTE_HORIZONTALLY.text;
                    break;
                }

                case EditorConstants.DISTRIBUTE_VERTICALLY.value: {
                    Distribution.distributeVertically(widgets, containerSize, experienceSize, isAlignedToScene);
                    actionName = EditorConstants.DISTRIBUTE_VERTICALLY.text;
                    break;
                }

                default: {
                    Preconditions.warn(`DefaultSwitchWarn - Align or distribution options of '${e.val}' was not recognized.`);
                    break;
                }
            }

            this._triggerPositioningEvent(actionName, "WidgetMenuAlignControlDropdown");
        },

        /**
         * Send the Mixpanel when a position change is applied.
         *
         * @param {String} actionName - The name for of the action @see EditorConstants#text
         * @param {String} triggerOrigin - Name of where the event is triggered from.
         *
         * @fires EditorEvents#update:widget
         * @type {Object}
         * @property {String} [widgetType]                  The type of the widget if the selection is one.
         * @property {String} updatedProperty               The property of the widget which has been modified. @see EditorConstants#text
         * @property {Number} experienceId                  The experience id.
         * @property {Number} experienceVersion             The experience version.
         * @property {Number} experiencePushedVersion       The experience pushed version sceneNumber.
         * @property {Number} sceneNumber                   The scene number.
         * @property {Boolean} isGrouped                    If the widget is a group
         * @property {Number} [numberOfWidgetInGroup]       If the widget is a group then the number of widget inside is sent.
         * @property {Boolean} isSelection                  If it's a selection of widget
         * @property {Number} [numberOfWidgetInSelection]   If it's a selection of widget the number of widget selected.
         * @property {String} triggerOrigin                 The origin of the event.
         * @private
         */
        _triggerPositioningEvent(actionName, triggerOrigin) {
            const viewModel         = this.viewScope.viewModel;
            const selectedModels    = viewModel.selection.get("models");
            const group             = this.model.get("group");
            let eventProperties     = {
                updatedProperty:            camelize(actionName),
                experienceId:               this._experience.get("id"),
                experienceVersion:          this._experience.get("version"),
                experiencePushedVersion:    this._experience.get("pushedVersion"),
                sceneNumber:                viewModel.get("sceneNumber"),
                isGrouped:                  group !== undefined,
                isMultipleWidgets:          selectedModels > 1,
                triggerOrigin
            };

            if (group) {
                eventProperties.numberOfWidgetInGroup = group.length;
            } else if (selectedModels.length > 1) {
                eventProperties.numberOfWidgetInSelection = selectedModels.length;
            } else if (selectedModels.lentgh === 1) {
                eventProperties.widgetType = this.model.get("type");
            }

            EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
        },

        onBeforeClose() {
            this.ui.arrangeControls.off("select2-selecting");
            this.ui.arrangeControls.off("select2-close");
            this.ui.alignControls.off("select2-selecting");
            this.ui.alignControls.off("select2-close");
        },

        onClosing() {
            const viewModel         = this.viewScope.viewModel;
            const eventProperties   = {
                widgetType:             this.model.get("type"),
                experienceId:           this._experience.get("id"),
                experienceVersion:      this._experience.get("version"),
                experiencePushedVersion: this._experience.get("pushedVersion"),
                sceneNumber:            viewModel.get("sceneNumber")
            };

            if (this.positionInputChanged) {
                extend(eventProperties, {
                    updatedProperty:    "position",
                    updatedValue:       `${this.model.get("left")}-${this.model.get("top")}`,
                    triggerOrigin:      "WidgetMenuPositionInputField"
                });
                EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
            }
            if (this.dimensionsInputChanged) {
                extend(eventProperties, {
                    updatedProperty:    "dimensions",
                    updatedValue:       this.model.get("width"),
                    triggerOrigin:      "WidgetMenuDimensionsInputField"
                });
                EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
            }
            if (this.rotationInputChanged) {
                extend(eventProperties, {
                    updatedProperty:    "rotation",
                    updatedValue:       this.model.get("rotation"),
                    triggerOrigin:      "WidgetMenuRotationInputField"
                });
                EditorEvents.trigger(EditorEvents.constants.UPDATE_WIDGET, eventProperties);
            }
        }
    }, {
        EXPAND_PROPERTY
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/SizeAndPositionPropertiesView.js